import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePrivy, useWallets, useCreateWallet } from '@privy-io/react-auth';
import UploadRecord from '../components/UploadRecord';
import PortalGate from '../components/PortalGate';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import {
  decryptFile,
  decryptFileV2,
  getOrDeriveKEK,
  unwrapFileKey,
  importPlainKey,
  wrapFileKey,
  generateShareKey,
  wrapFileKeyWithShareKey,
  generateECDHKeyPair,
  wrapECDHPrivateKey,
  wrapFileKeyForRecipient,
} from '../lib/webCryptoEncryption';
import { createShareLink, getShareLinksForRecord, revokeShareLink, getAllShareLinksForUser } from '../lib/shareLinks';
import { logAccess, getAccessLog } from '../lib/auditLog';

const T = '#00A19C';
const T_DARK = '#007F7B';
const FONT = '"Gotham SSm A", "Gotham SSm B", system-ui, -apple-system, sans-serif';
const PRIVY_CONFIGURED = !!process.env.REACT_APP_PRIVY_APP_ID;

// ─── Record type taxonomy ─────────────────────────────────────────────────────
export const RECORD_TYPES = [
  { value: 'lab',          label: 'Lab Results',    color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  { value: 'imaging',      label: 'Imaging',         color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe' },
  { value: 'vaccination',  label: 'Vaccination',     color: '#059669', bg: '#ecfdf5', border: '#6ee7b7' },
  { value: 'prescription', label: 'Prescription',    color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
  { value: 'visit',        label: 'Visit Summary',   color: T,         bg: '#f0fdfb', border: '#99f6e4' },
  { value: 'other',        label: 'Other',           color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
];

function getRecordType(value) {
  return RECORD_TYPES.find(t => t.value === value) || null;
}

// ─── Window width hook (mobile detection) ────────────────────────────────────
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return width;
}

// ─── Analytics helper ─────────────────────────────────────────────────────────
function track(eventName, params = {}) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
}

// ─── Shield icon ─────────────────────────────────────────────────────────────
function ShieldIcon({ size = 20, color = 'white' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0d1117',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      padding: '24px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Background glow */}
      <div style={{
        position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
        width: '600px', height: '400px',
        background: `radial-gradient(ellipse at center, ${T}18 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Logo */}
      <div style={{ marginBottom: '48px', textAlign: 'center', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '10px',
            background: T, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 24px ${T}60`,
          }}>
            <ShieldIcon size={22} />
          </div>
          <span style={{ fontSize: '22px', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>
            Vewr Health
          </span>
        </div>
      </div>

      {/* Card */}
      <div style={{
        background: '#161b22',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '20px',
        padding: '48px 44px',
        width: '100%',
        maxWidth: '420px',
        textAlign: 'center',
        position: 'relative',
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
      }}>
        {/* Top accent line */}
        <div style={{
          position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: '60px', height: '3px', background: T, borderRadius: '0 0 4px 4px',
        }} />

        <h1 style={{
          color: 'white', fontSize: '26px', fontWeight: '700',
          margin: '0 0 12px 0', letterSpacing: '-0.5px',
        }}>
          Sign in to your vault
        </h1>
        <p style={{
          color: 'rgba(255,255,255,0.45)', fontSize: '15px',
          margin: '0 0 36px 0', lineHeight: '1.65',
        }}>
          We'll send a one-time code to your email.<br />No password, no app to download.
        </p>

        <button
          onClick={onLogin}
          style={{
            width: '100%', padding: '15px 24px',
            background: T, color: 'white', border: 'none',
            borderRadius: '10px', fontSize: '15px', fontWeight: '600',
            cursor: 'pointer', letterSpacing: '0.2px',
            transition: 'background 0.2s, transform 0.1s',
            boxShadow: `0 4px 20px ${T}50`,
            fontFamily: FONT,
          }}
          onMouseEnter={e => e.currentTarget.style.background = T_DARK}
          onMouseLeave={e => e.currentTarget.style.background = T}
          onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
          onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          Continue with Email →
        </button>

        {/* Trust pills */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '28px', flexWrap: 'wrap' }}>
          {['Browser-encrypted', 'No password', 'Works on any device'].map(pill => (
            <span key={pill} style={{
              fontSize: '12px', color: 'rgba(255,255,255,0.35)',
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              padding: '4px 10px', borderRadius: '100px',
            }}>
              {pill}
            </span>
          ))}
        </div>
      </div>

      <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '13px', marginTop: '32px', textAlign: 'center', maxWidth: '340px', lineHeight: '1.6' }}>
        Your files are encrypted before they leave your device. Not even we can read them.
      </p>

      <a href="/" style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        marginTop: '24px', color: 'rgba(255,255,255,0.25)', fontSize: '13px',
        textDecoration: 'none', transition: 'color 0.2s',
      }}
        onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}
      >
        ← Back to home
      </a>
    </div>
  );
}

// ─── Setup Notice (no Privy App ID) ──────────────────────────────────────────
function SetupNotice() {
  return (
    <div style={{
      minHeight: '100vh', background: '#0d1117',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: FONT, padding: '24px',
    }}>
      <div style={{
        background: '#161b22', border: '1px solid rgba(255,165,0,0.3)',
        borderRadius: '16px', padding: '40px', maxWidth: '460px', textAlign: 'center',
      }}>
        <div style={{ fontSize: '36px', marginBottom: '16px' }}>⚙️</div>
        <h2 style={{ color: 'white', fontSize: '20px', fontWeight: '700', margin: '0 0 12px 0' }}>
          Privy App ID Required
        </h2>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', lineHeight: '1.7', margin: '0 0 20px 0' }}>
          Add your Privy App ID to <code style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 6px', borderRadius: '4px' }}>.env</code> to enable authentication:
        </p>
        <div style={{
          background: '#0d1117', borderRadius: '8px', padding: '14px 16px',
          fontFamily: 'monospace', fontSize: '13px', color: T, textAlign: 'left',
        }}>
          REACT_APP_PRIVY_APP_ID=your-app-id-here
        </div>
        <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '12px', marginTop: '16px', marginBottom: 0 }}>
          Get your App ID at <span style={{ color: T }}>dashboard.privy.io</span>
        </p>
      </div>
    </div>
  );
}

// ─── Records List ─────────────────────────────────────────────────────────────
const IPFS_GATEWAYS = [
  'https://gateway.pinata.cloud/ipfs/',
  'https://ipfs.io/ipfs/',
  'https://cloudflare-ipfs.com/ipfs/',
];

async function fetchFromIPFS(cid) {
  for (const gateway of IPFS_GATEWAYS) {
    try {
      const res = await fetch(`${gateway}${cid}`);
      if (res.ok) return res.arrayBuffer();
    } catch (e) {}
  }
  throw new Error('Could not retrieve file from IPFS. Try again in a moment.');
}

// ─── Share Panel ──────────────────────────────────────────────────────────────
const EXPIRY_PRESETS = [
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 168 },
  { label: '30 days', hours: 720 },
];

// Returns a datetime-local string for "now + 1 hour" as the minimum allowed custom date
function minCustomDatetime() {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  // Format as YYYY-MM-DDTHH:MM (datetime-local format, local time)
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function SharePanel({ record, userId, onClose }) {
  const { wallets } = useWallets();
  const [expiryMode, setExpiryMode] = useState('preset'); // 'preset' | 'custom'
  const [selectedPreset, setSelectedPreset] = useState(EXPIRY_PRESETS[0]);
  const [customDatetime, setCustomDatetime] = useState(''); // datetime-local value
  const [creating, setCreating] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activeLinks, setActiveLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(true);
  const [revoking, setRevoking] = useState({});
  const [shareError, setShareError] = useState('');
  const [copiedLink, setCopiedLink] = useState(null); // link id that was just copied

  const loadLinks = useCallback(async () => {
    setLoadingLinks(true);
    const { data } = await getShareLinksForRecord(record.id, userId);
    setActiveLinks(data || []);
    setLoadingLinks(false);
  }, [record.id, userId]);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  // Compute hours from either preset or custom datetime
  const getExpiryHours = () => {
    if (expiryMode === 'preset') return selectedPreset.hours;
    if (!customDatetime) return null;
    const target = new Date(customDatetime);
    const diffMs = target.getTime() - Date.now();
    if (diffMs <= 0) return null;
    return diffMs / (1000 * 60 * 60); // fractional hours is fine — stored as ISO timestamp
  };

  const handleCreate = async () => {
    setShareError('');
    setGeneratedUrl(null);

    const hours = getExpiryHours();
    if (hours === null) {
      setShareError(expiryMode === 'custom'
        ? 'Please pick a future date and time.'
        : 'Invalid expiry. Please choose an option.');
      return;
    }

    setCreating(true);
    try {
      // 1. Get the embedded wallet
      const embeddedWallet = wallets.find(w => w.walletClientType === 'privy');
      if (!embeddedWallet) throw new Error('Wallet not ready — please try again.');

      // 2. Resolve the file key (v2: unwrap with KEK; v1: import plain key)
      const keyVersion = record.metadata?.keyVersion || 1;
      let fileKey;
      if (keyVersion >= 2) {
        const kek = await getOrDeriveKEK(embeddedWallet, userId);
        fileKey = await unwrapFileKey(record.metadata.encryptedKey, kek);
      } else {
        fileKey = await importPlainKey(record.metadata.encryptedKey);
      }

      // 3. Generate a one-time share key and re-wrap the file key with it
      const { shareKey, shareKeyB64 } = await generateShareKey();
      const wrappedKeyForShare = await wrapFileKeyWithShareKey(fileKey, shareKey);

      // 4. Store the share-key-wrapped file key in Supabase
      const { data, error } = await createShareLink(record, userId, hours, wrappedKeyForShare);
      if (error || !data) throw new Error('Failed to create share link. Please try again.');

      // 5. The share key goes in the URL as a query param — GTM mangles fragments
      const url = `${window.location.origin}/share/${data.token}?k=${encodeURIComponent(shareKeyB64)}`;
      setGeneratedUrl(url);
      track('record_shared', { file_type: record.metadata?.originalFileType, expiry_hours: Math.round(hours) });
      logAccess({ action: 'share_created', userId, recordId: record.id, fileName: record.metadata?.originalFileName, fileType: record.metadata?.originalFileType, shareToken: data.token });
      await loadLinks();
    } catch (err) {
      setShareError(err.message || 'Failed to create share link. Please try again.');
    }
    setCreating(false);
  };

  const handleCopy = () => {
    if (!generatedUrl) return;
    navigator.clipboard.writeText(generatedUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleRevoke = async (linkId) => {
    setRevoking(prev => ({ ...prev, [linkId]: true }));
    const link = activeLinks.find(l => l.id === linkId);
    await revokeShareLink(linkId);
    track('share_link_revoked');
    logAccess({ action: 'share_revoked', userId, recordId: record.id, fileName: link?.file_name, fileType: link?.file_type, shareToken: link?.token });
    await loadLinks();
    setRevoking(prev => ({ ...prev, [linkId]: false }));
  };

  const handleCopyLink = (link) => {
    const url = `${window.location.origin}/share/${link.token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLink(link.id);
      setTimeout(() => setCopiedLink(id => id === link.id ? null : id), 2000);
    });
  };

  const formatShortExpiry = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const isCustomMode = expiryMode === 'custom';
  const canCreate = isCustomMode ? !!customDatetime : true;

  return (
    <div style={{
      background: '#f8faff', border: `1px solid ${T}25`,
      borderRadius: '12px', padding: '20px 22px',
      marginTop: '4px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Share Record</span>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#9ca3af', fontSize: '18px', lineHeight: 1, padding: '2px 4px',
        }}>×</button>
      </div>

      {/* Expiry picker */}
      <div style={{ marginBottom: '14px' }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Link expires
        </div>

        {/* Preset + Custom toggle row */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: isCustomMode ? '10px' : '0' }}>
          {EXPIRY_PRESETS.map(opt => {
            const active = !isCustomMode && selectedPreset.hours === opt.hours;
            return (
              <button
                key={opt.hours}
                onClick={() => { setExpiryMode('preset'); setSelectedPreset(opt); setShareError(''); }}
                style={{
                  flex: 1, padding: '8px 6px', borderRadius: '8px',
                  cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: FONT,
                  background: active ? T : 'white',
                  color: active ? 'white' : '#374151',
                  boxShadow: active ? `0 2px 8px ${T}40` : '0 1px 3px rgba(0,0,0,0.07)',
                  border: `1px solid ${active ? T : '#e5e7eb'}`,
                  transition: 'all 0.15s',
                }}
              >
                {opt.label}
              </button>
            );
          })}
          {/* Custom button */}
          <button
            onClick={() => {
              setExpiryMode('custom');
              if (!customDatetime) setCustomDatetime(minCustomDatetime());
              setShareError('');
            }}
            style={{
              flex: 1, padding: '8px 6px', borderRadius: '8px',
              cursor: 'pointer', fontSize: '13px', fontWeight: '500', fontFamily: FONT,
              background: isCustomMode ? T : 'white',
              color: isCustomMode ? 'white' : '#374151',
              boxShadow: isCustomMode ? `0 2px 8px ${T}40` : '0 1px 3px rgba(0,0,0,0.07)',
              border: `1px solid ${isCustomMode ? T : '#e5e7eb'}`,
              transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Custom
          </button>
        </div>

        {/* Custom datetime input */}
        {isCustomMode && (
          <div style={{ position: 'relative' }}>
            <input
              type="datetime-local"
              value={customDatetime}
              min={minCustomDatetime()}
              onChange={e => { setCustomDatetime(e.target.value); setShareError(''); }}
              style={{
                width: '100%', padding: '10px 14px',
                border: `1px solid ${T}40`, borderRadius: '9px',
                fontSize: '14px', color: '#111827', background: 'white',
                fontFamily: FONT, outline: 'none', boxSizing: 'border-box',
                cursor: 'pointer',
              }}
            />
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: '6px 0 0 2px' }}>
              Times are in your local timezone.
            </p>
          </div>
        )}
      </div>

      {/* Generate button */}
      <button
        onClick={handleCreate}
        disabled={creating || !canCreate}
        style={{
          width: '100%', padding: '11px 18px',
          background: (creating || !canCreate) ? '#e5e7eb' : T,
          color: (creating || !canCreate) ? '#9ca3af' : 'white',
          border: 'none', borderRadius: '9px',
          fontSize: '14px', fontWeight: '600', cursor: (creating || !canCreate) ? 'not-allowed' : 'pointer',
          fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
          transition: 'all 0.2s',
          boxShadow: (creating || !canCreate) ? 'none' : `0 3px 10px ${T}35`,
          marginBottom: '14px',
        }}
      >
        {creating ? (
          <>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', border: `2px solid #9ca3af`, borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />
            Creating link…
          </>
        ) : (
          <>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            Generate share link
          </>
        )}
      </button>

      {/* Error */}
      {shareError && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '13px', color: '#dc2626', marginBottom: '12px' }}>
          {shareError}
        </div>
      )}

      {/* Generated URL */}
      {generatedUrl && (
        <div style={{
          background: 'white', border: `1px solid ${T}30`, borderRadius: '10px',
          padding: '14px', marginBottom: '14px',
        }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Share link ready
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              flex: 1, fontFamily: 'monospace', fontSize: '12px', color: '#374151',
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '7px',
              padding: '8px 10px', wordBreak: 'break-all', lineHeight: '1.4',
            }}>
              {generatedUrl}
            </div>
            <button
              onClick={handleCopy}
              style={{
                flexShrink: 0, padding: '9px 14px', borderRadius: '8px',
                border: `1px solid ${copied ? T : '#e5e7eb'}`,
                background: copied ? `${T}12` : 'white',
                color: copied ? T : '#374151',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                fontFamily: FONT, transition: 'all 0.2s', whiteSpace: 'nowrap',
              }}
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <p style={{ fontSize: '12px', color: '#9ca3af', margin: '8px 0 0 0' }}>
            Anyone with this link can view the file until it expires. Revoke it below to cut off access immediately.
          </p>
        </div>
      )}

      {/* Active links list */}
      {!loadingLinks && activeLinks.length > 0 && (
        <div>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Active links ({activeLinks.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {activeLinks.map(link => {
              const views = link.view_count || 0;
              const lastViewed = link.last_viewed_at
                ? new Date(link.last_viewed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                : null;
              const isCopied = copiedLink === link.id;

              return (
                <div key={link.id} style={{
                  background: 'white', border: '1px solid #e5e7eb',
                  borderRadius: '8px', padding: '12px 14px',
                }}>
                  {/* Top: expiry + actions */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '10px' }}>
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                      Expires {formatShortExpiry(link.expires_at)}
                    </div>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button
                        onClick={() => handleCopyLink(link)}
                        style={{
                          padding: '4px 11px', borderRadius: '6px',
                          border: `1px solid ${isCopied ? T : '#e5e7eb'}`,
                          background: isCopied ? `${T}12` : 'white',
                          color: isCopied ? T : '#374151',
                          fontSize: '12px', fontWeight: '600',
                          cursor: 'pointer', fontFamily: FONT, transition: 'all 0.2s',
                          display: 'flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        {isCopied ? (
                          <>✓ Copied</>
                        ) : (
                          <>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                            Copy link
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => handleRevoke(link.id)}
                        disabled={revoking[link.id]}
                        style={{
                          padding: '4px 11px', borderRadius: '6px', border: '1px solid #fca5a5',
                          background: revoking[link.id] ? '#f9fafb' : '#fff5f5',
                          color: '#dc2626', fontSize: '12px', fontWeight: '600',
                          cursor: revoking[link.id] ? 'not-allowed' : 'pointer',
                          fontFamily: FONT, transition: 'all 0.15s',
                        }}
                      >
                        {revoking[link.id] ? '…' : 'Revoke'}
                      </button>
                    </div>
                  </div>

                  {/* Bottom: view stats */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    paddingTop: '8px', borderTop: '1px solid #f3f4f6',
                  }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={views > 0 ? T : '#d1d5db'} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span style={{ fontSize: '11px', fontWeight: '600', color: views > 0 ? T : '#9ca3af' }}>
                      {views === 0 ? 'Not opened yet' : `Opened ${views} ${views === 1 ? 'time' : 'times'}`}
                    </span>
                    {lastViewed && (
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                        · Last opened {lastViewed}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Share With Provider Panel ────────────────────────────────────────────────
function ShareWithProviderPanel({ record, userId, userEmail, wallets, onClose }) {
  const [providers, setProviders] = useState([]);
  const [existingAccess, setExistingAccess] = useState(new Set());
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [sharing, setSharing] = useState({});
  const [shareError, setShareError] = useState({});
  const [confirmProvider, setConfirmProvider] = useState(null);

  const loadProviders = async () => {
    setLoadingProviders(true);
    const { data: connections } = await supabase
      .from('connections')
      .select('*')
      .eq('patient_email', userEmail)
      .eq('status', 'accepted');

    if (!connections || connections.length === 0) {
      setProviders([]);
      setLoadingProviders(false);
      return;
    }

    const providerIds = connections.map(c => c.provider_id);
    const { data: provRows } = await supabase
      .from('providers')
      .select('user_id, first_name, last_name, credential, taxonomy, ecdh_public_key')
      .in('user_id', providerIds);

    setProviders(provRows || []);
    setLoadingProviders(false);
  };

  const loadExistingAccess = async () => {
    const { data: existing } = await supabase
      .from('provider_access')
      .select('provider_id')
      .eq('record_id', record.id);
    setExistingAccess(new Set((existing || []).map(r => r.provider_id)));
  };

  useEffect(() => {
    loadProviders();
    loadExistingAccess();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShare = async (provider) => {
    setSharing(s => ({ ...s, [provider.user_id]: true }));
    setShareError(e => ({ ...e, [provider.user_id]: null }));
    try {
      const embeddedWallet = wallets.find(w => w.walletClientType === 'privy');
      if (!embeddedWallet) throw new Error('Wallet not ready — please try again.');

      let fileKey;
      const keyVersion = record.metadata?.keyVersion || 1;
      if (keyVersion >= 2) {
        const kek = await getOrDeriveKEK(embeddedWallet, userId);
        fileKey = await unwrapFileKey(record.metadata.encryptedKey, kek);
      } else {
        fileKey = await importPlainKey(record.metadata.encryptedKey);
      }

      const { ephemeralPublicKeyB64, wrappedFileKeyB64 } = await wrapFileKeyForRecipient(fileKey, provider.ecdh_public_key);

      await supabase.from('provider_access').upsert({
        provider_id: provider.user_id,
        patient_id: userId,
        record_id: record.id,
        cid: record.cid,
        encrypted_key: wrappedFileKeyB64,
        ephemeral_public_key: ephemeralPublicKeyB64,
        iv: record.metadata.iv,
        file_name: record.metadata.originalFileName,
        file_type: record.metadata.originalFileType,
        record_type: record.metadata.recordType || null,
      }, { onConflict: 'provider_id,record_id' });

      await loadExistingAccess();
      setConfirmProvider(null);
    } catch (e) {
      setShareError(err => ({ ...err, [provider.user_id]: e.message || 'Share failed.' }));
      setConfirmProvider(null);
    }
    setSharing(s => ({ ...s, [provider.user_id]: false }));
  };

  const renderConfirmView = () => {
    const pName = [confirmProvider.first_name, confirmProvider.last_name].filter(Boolean).join(' ');
    const rName = record.metadata?.recordName || record.metadata?.originalFileName || 'Medical Record';
    const rType = getRecordType(record.metadata?.recordType);
    const rDate = record.metadata?.recordDate
      ? new Date(record.metadata.recordDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : null;
    const isSharingThis = sharing[confirmProvider.user_id];
    const err = shareError[confirmProvider.user_id];
    return (
      <div>
        <button onClick={() => setConfirmProvider(null)} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#9ca3af', fontSize: '13px', fontFamily: FONT,
          display: 'flex', alignItems: 'center', gap: '4px',
          padding: '0', marginBottom: '16px',
        }}>
          ← Back to providers
        </button>

        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
            Sharing with
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '10px', background: '#f9fafb', border: '1px solid #f3f4f6' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: `${T}15`, border: `1px solid ${T}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>🩺</div>
            <div>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>
                {pName ? `${pName}${confirmProvider.credential ? `, ${confirmProvider.credential}` : ''}` : 'Provider'}
              </div>
              {confirmProvider.taxonomy && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{confirmProvider.taxonomy}</div>}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
            Record being shared
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '10px', background: '#f9fafb', border: '1px solid #f3f4f6' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: rType ? rType.bg : '#f0fdfb', border: `1px solid ${rType ? rType.border : `${T}25`}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={rType ? rType.color : T} strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap', marginBottom: '3px' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>{rName}</span>
                {rType && <span style={{ fontSize: '11px', fontWeight: '600', padding: '1px 8px', borderRadius: '100px', background: rType.bg, color: rType.color, border: `1px solid ${rType.border}` }}>{rType.label}</span>}
              </div>
              {rDate && <div style={{ fontSize: '12px', color: '#9ca3af' }}>Record date: {rDate}</div>}
            </div>
          </div>
        </div>

        <div style={{ background: `${T}08`, border: `1px solid ${T}25`, borderRadius: '10px', padding: '12px 14px', marginBottom: '14px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: '2px', flexShrink: 0 }}>
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.6' }}>
            <strong>{pName || 'This provider'}</strong> will only be able to access this one file.
            Your other records stay private unless you explicitly share them separately.
          </p>
        </div>

        {err && <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '13px', color: '#dc2626', marginBottom: '12px' }}>{err}</div>}

        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setConfirmProvider(null)} disabled={isSharingThis} style={{ flex: 1, padding: '10px 14px', background: 'white', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '9px', fontSize: '14px', fontWeight: '500', cursor: isSharingThis ? 'not-allowed' : 'pointer', fontFamily: FONT }}>
            Cancel
          </button>
          <button
            onClick={() => handleShare(confirmProvider)}
            disabled={isSharingThis || !confirmProvider.ecdh_public_key}
            style={{ flex: 2, padding: '10px 18px', background: isSharingThis ? '#e5e7eb' : T, color: isSharingThis ? '#9ca3af' : 'white', border: 'none', borderRadius: '9px', fontSize: '14px', fontWeight: '600', cursor: (isSharingThis || !confirmProvider.ecdh_public_key) ? 'not-allowed' : 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', boxShadow: (isSharingThis || !confirmProvider.ecdh_public_key) ? 'none' : `0 3px 10px ${T}35` }}
          >
            {isSharingThis ? (
              <><div style={{ width: '12px', height: '12px', borderRadius: '50%', border: '2px solid #9ca3af', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />Sharing…</>
            ) : 'Confirm share →'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      background: 'white', border: `1px solid ${T}25`,
      borderRadius: '12px', padding: '20px 22px',
      marginTop: '4px',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2" strokeLinecap="round">
            <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
            <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" />
            <circle cx="20" cy="10" r="2" />
          </svg>
          <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Share with Provider</span>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#9ca3af', fontSize: '18px', lineHeight: 1, padding: '2px 4px',
        }}>×</button>
      </div>

      {loadingProviders ? (
        <div style={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>Loading providers…</div>
      ) : providers.length === 0 ? (
        <div style={{ color: '#6b7280', fontSize: '13px', textAlign: 'center', padding: '16px 0', lineHeight: '1.5' }}>
          No connected providers yet. Accept a connection request first.
        </div>
      ) : confirmProvider ? renderConfirmView() : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {providers.map(provider => {
            const name = [provider.first_name, provider.last_name].filter(Boolean).join(' ');
            const alreadyShared = existingAccess.has(provider.user_id);
            const isSharingThis = sharing[provider.user_id];
            const err = shareError[provider.user_id];
            return (
              <div key={provider.user_id} style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                padding: '14px 16px', borderRadius: '10px',
                background: '#f9fafb', border: '1px solid #f3f4f6',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>
                    {name ? `${name}${provider.credential ? `, ${provider.credential}` : ''}` : 'Provider'}
                  </div>
                  {provider.taxonomy && (
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{provider.taxonomy}</div>
                  )}
                  {err && <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '3px' }}>{err}</div>}
                </div>
                {alreadyShared ? (
                  <span style={{
                    fontSize: '12px', fontWeight: '600', padding: '5px 12px',
                    borderRadius: '8px', background: '#ecfdf5', color: '#059669',
                    border: '1px solid #6ee7b7', whiteSpace: 'nowrap', flexShrink: 0,
                  }}>
                    Shared ✓
                  </span>
                ) : (
                  <button
                    onClick={() => setConfirmProvider(provider)}
                    disabled={isSharingThis || !provider.ecdh_public_key}
                    style={{
                      padding: '7px 16px', background: isSharingThis ? '#f3f4f6' : T,
                      color: isSharingThis ? '#9ca3af' : 'white',
                      border: 'none', borderRadius: '8px',
                      fontSize: '13px', fontWeight: '600',
                      cursor: (isSharingThis || !provider.ecdh_public_key) ? 'not-allowed' : 'pointer',
                      fontFamily: FONT, whiteSpace: 'nowrap', flexShrink: 0,
                    }}
                  >
                    {isSharingThis ? 'Sharing…' : !provider.ecdh_public_key ? 'Not ready' : 'Share'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RecordsList({ records, onDelete, onUpdate, userId = '', userEmail = '' }) {
  const { wallets } = useWallets();
  const [downloading, setDownloading] = useState({});
  const [dlError, setDlError] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState({});
  const [shareOpen, setShareOpen] = useState(null);
  const [providerShareOpen, setProviderShareOpen] = useState(null);
  const [editOpen, setEditOpen] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [saving, setSaving] = useState({});
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState(null); // null = all
  const isMobile = useWindowWidth() < 640;

  const handleDownload = async (record) => {
    const { id, cid, metadata } = record;
    const { encryptedKey, iv, originalFileName, originalFileType } = metadata || {};

    if (!encryptedKey || !iv) {
      setDlError(prev => ({ ...prev, [id]: 'Missing encryption key — this record cannot be decrypted.' }));
      return;
    }

    setDlError(prev => ({ ...prev, [id]: null }));

    try {
      setDownloading(prev => ({ ...prev, [id]: 'fetching' }));
      const encryptedData = await fetchFromIPFS(cid);

      setDownloading(prev => ({ ...prev, [id]: 'decrypting' }));

      // v2: unwrap file key with KEK; v1: use plain base64 key directly
      const keyVersion = metadata?.keyVersion || 1;
      let decryptedData;
      if (keyVersion >= 2) {
        const embeddedWallet = wallets.find(w => w.walletClientType === 'privy');
        if (!embeddedWallet) throw new Error('Your vault is still loading. Please try again.');
        const kek = await getOrDeriveKEK(embeddedWallet, userId);
        decryptedData = await decryptFileV2(encryptedData, encryptedKey, iv, kek);
      } else {
        decryptedData = await decryptFile(encryptedData, encryptedKey, iv);
      }

      // Trigger browser download
      track('record_downloaded', { file_type: originalFileType });
      logAccess({ action: 'download', userId, recordId: id, fileName: originalFileName, fileType: originalFileType });
      const blob = new Blob([decryptedData], { type: originalFileType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = originalFileName || `vewr-record-${cid.slice(0, 8)}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setDlError(prev => ({ ...prev, [id]: err.message }));
    } finally {
      setDownloading(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleDeleteClick = (record) => {
    if (confirmDelete === record.id) {
      // Second click — execute delete
      setDeleting(prev => ({ ...prev, [record.id]: true }));
      setConfirmDelete(null);
      setShareOpen(null);
      onDelete(record).finally(() => {
        setDeleting(prev => ({ ...prev, [record.id]: false }));
      });
    } else {
      // First click — arm confirmation
      setConfirmDelete(record.id);
      // Auto-cancel after 3 s if user doesn't confirm
      setTimeout(() => setConfirmDelete(id => id === record.id ? null : id), 3000);
    }
  };

  const toggleShare = (recordId) => {
    setShareOpen(prev => prev === recordId ? null : recordId);
    setProviderShareOpen(null);
    setConfirmDelete(null);
  };

  const openEdit = (record) => {
    setEditOpen(record.id);
    setEditFields(prev => ({
      ...prev,
      [record.id]: {
        recordName: record.metadata?.recordName || '',
        recordDate: record.metadata?.recordDate || '',
        recordNotes: record.metadata?.recordNotes || '',
        recordType: record.metadata?.recordType || null,
      },
    }));
    setShareOpen(null);
    setProviderShareOpen(null);
    setConfirmDelete(null);
  };

  const handleSaveEdit = async (record) => {
    const fields = editFields[record.id] || {};
    setSaving(prev => ({ ...prev, [record.id]: true }));
    const updatedMetadata = {
      ...record.metadata,
      recordName: fields.recordName?.trim() || null,
      recordDate: fields.recordDate || null,
      recordNotes: fields.recordNotes?.trim() || null,
      recordType: fields.recordType !== undefined ? fields.recordType : (record.metadata?.recordType || null),
    };
    if (isSupabaseConfigured) {
      await supabase.from('records').update({ metadata: updatedMetadata }).eq('id', record.id);
    }
    if (typeof onUpdate === 'function') onUpdate(record.id, updatedMetadata);
    setSaving(prev => ({ ...prev, [record.id]: false }));
    setEditOpen(null);
  };

  if (records.length === 0) {
    return (
      <div style={{
        background: 'white', borderRadius: '16px',
        border: '1px solid #e5e7eb', padding: '72px 32px', textAlign: 'center',
      }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '16px',
          background: '#f0fdfb', border: `1px solid ${T}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="1.8">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
        </div>
        <h3 style={{ color: '#111827', fontSize: '17px', fontWeight: '600', margin: '0 0 8px 0' }}>
          No records yet
        </h3>
        <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>
          Upload your first medical file to get started.
        </p>
      </div>
    );
  }

  // Filtered + searched records
  const visibleRecords = records.filter(r => {
    const name = r.metadata?.recordName || r.metadata?.originalFileName || '';
    const notes = r.metadata?.recordNotes || '';
    const matchesSearch = !search || name.toLowerCase().includes(search.toLowerCase()) || notes.toLowerCase().includes(search.toLowerCase());
    const matchesType = !filterType || r.metadata?.recordType === filterType;
    return matchesSearch && matchesType;
  });

  // Which types actually exist in the list
  const presentTypes = RECORD_TYPES.filter(t => records.some(r => r.metadata?.recordType === t.value));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .dl-btn:hover { background: #f0fdfb !important; border-color: ${T} !important; color: ${T} !important; }
        .share-btn:hover { background: #f0fdfb !important; border-color: ${T} !important; color: ${T} !important; }
        .share-btn-active { background: ${T}12 !important; border-color: ${T} !important; color: ${T} !important; }
        .edit-btn:hover { background: #f9fafb !important; border-color: #9ca3af !important; color: #374151 !important; }
        .edit-btn-active { background: #f3f4f6 !important; border-color: #9ca3af !important; color: #111827 !important; }
        .del-btn:hover { background: #fef2f2 !important; border-color: #fca5a5 !important; color: #dc2626 !important; }
        .del-confirm:hover { background: #dc2626 !important; color: white !important; }
        .filter-pill:hover { border-color: #9ca3af !important; color: #374151 !important; }
      `}</style>

      {/* Search + filter — shown when 10+ records OR any type tags exist */}
      {(records.length >= 10 || presentTypes.length > 0) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '4px' }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.2" strokeLinecap="round" style={{ position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search records…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '10px 14px 10px 38px',
                border: '1px solid #e5e7eb', borderRadius: '10px',
                fontSize: '14px', color: '#111827', background: 'white',
                fontFamily: FONT, outline: 'none', boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = T}
              onBlur={e => e.target.style.borderColor = '#e5e7eb'}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '16px', lineHeight: 1, padding: '2px' }}>×</button>
            )}
          </div>

          {/* Type filter pills — only shown if any records have tags */}
          {presentTypes.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                className="filter-pill"
                onClick={() => setFilterType(null)}
                style={{
                  padding: '5px 13px', borderRadius: '100px', border: `1px solid ${!filterType ? T : '#e5e7eb'}`,
                  background: !filterType ? T : 'white', color: !filterType ? 'white' : '#6b7280',
                  fontSize: '12px', fontWeight: '500', cursor: 'pointer', fontFamily: FONT, transition: 'all 0.15s',
                }}
              >
                All
              </button>
              {presentTypes.map(t => (
                <button
                  key={t.value}
                  className="filter-pill"
                  onClick={() => setFilterType(filterType === t.value ? null : t.value)}
                  style={{
                    padding: '5px 13px', borderRadius: '100px',
                    border: `1px solid ${filterType === t.value ? t.color : '#e5e7eb'}`,
                    background: filterType === t.value ? t.bg : 'white',
                    color: filterType === t.value ? t.color : '#6b7280',
                    fontSize: '12px', fontWeight: filterType === t.value ? '600' : '500',
                    cursor: 'pointer', fontFamily: FONT, transition: 'all 0.15s',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No results state */}
      {visibleRecords.length === 0 && (
        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '40px 24px', textAlign: 'center' }}>
          <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0 }}>No records match your search.</p>
          <button onClick={() => { setSearch(''); setFilterType(null); }} style={{ marginTop: '10px', background: 'none', border: 'none', color: T, fontSize: '13px', cursor: 'pointer', fontFamily: FONT }}>Clear filters</button>
        </div>
      )}

      {visibleRecords.map(record => {
        const name = record.metadata?.recordName || record.metadata?.originalFileName || 'Medical Record';
        const recordType = getRecordType(record.metadata?.recordType);
        const recordDate = record.metadata?.recordDate
          ? new Date(record.metadata.recordDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : null;
        const uploadedDate = new Date(record.uploadedAt).toLocaleDateString('en-US', {
          month: 'short', day: 'numeric', year: 'numeric',
        });
        const date = recordDate || uploadedDate;
        const dateLabel = recordDate ? 'Record date' : 'Uploaded';
        const notes = record.metadata?.recordNotes || null;
        const size = record.metadata?.originalFileSize
          ? `${(record.metadata.originalFileSize / 1024).toFixed(1)} KB`
          : '';
        const dlState = downloading[record.id];
        const err = dlError[record.id];
        const isConfirming = confirmDelete === record.id;
        const isDeletingThis = deleting[record.id];
        const isShareOpen = shareOpen === record.id;
        const isProviderShareOpen = providerShareOpen === record.id;
        const isEditOpen = editOpen === record.id;
        const isSaving = saving[record.id];
        const ef = editFields[record.id] || {};

        return (
          <div key={record.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={{
              background: 'white', borderRadius: '12px',
              border: `1px solid ${isConfirming ? '#fca5a5' : isShareOpen || isProviderShareOpen || isEditOpen ? `${T}40` : err ? '#fecaca' : '#e5e7eb'}`,
              padding: '16px 20px',
              transition: 'border-color 0.2s',
            }}>
              {/* Top row: file info + action buttons */}
              <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: '12px', flexDirection: isMobile ? 'column' : 'row' }}>
              {/* Left: file info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, width: isMobile ? '100%' : undefined }}>
                <div style={{
                  width: '38px', height: '38px', borderRadius: '9px',
                  background: recordType ? recordType.bg : '#f0fdfb',
                  border: `1px solid ${recordType ? recordType.border : `${T}25`}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={recordType ? recordType.color : T} strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '2px', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {name}
                    </span>
                    {recordType && (
                      <span style={{
                        fontSize: '11px', fontWeight: '600', padding: '1px 8px', borderRadius: '100px',
                        background: recordType.bg, color: recordType.color, border: `1px solid ${recordType.border}`,
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}>
                        {recordType.label}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                    {dateLabel}: {date}{size ? ` · ${size}` : ''}
                  </div>
                  {notes && (
                    <div style={{
                      fontSize: '12px', color: '#6b7280', marginTop: '3px',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {notes}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: action buttons */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, width: isMobile ? '100%' : undefined, justifyContent: isMobile ? 'flex-end' : undefined }}>

                {/* Share button */}
                {isSupabaseConfigured && (
                  <button
                    className={`share-btn${isShareOpen ? ' share-btn-active' : ''}`}
                    onClick={() => toggleShare(record.id)}
                    disabled={isDeletingThis}
                    title="Share record"
                    style={{
                      display: 'flex', alignItems: 'center', gap: isMobile ? '0' : '5px',
                      padding: isMobile ? '8px' : '7px 12px', borderRadius: '8px',
                      border: '1px solid #e5e7eb', background: 'white',
                      color: '#374151', fontSize: '13px', fontWeight: '500',
                      cursor: isDeletingThis ? 'not-allowed' : 'pointer',
                      fontFamily: FONT, transition: 'all 0.15s',
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                    {!isMobile && 'Share'}
                  </button>
                )}

                {/* Share with Provider button */}
                {isSupabaseConfigured && (
                  <button
                    className={`share-btn${isProviderShareOpen ? ' share-btn-active' : ''}`}
                    onClick={() => {
                      setProviderShareOpen(prev => prev === record.id ? null : record.id);
                      setShareOpen(null);
                      setConfirmDelete(null);
                    }}
                    disabled={isDeletingThis}
                    title="Share with provider"
                    style={{
                      display: 'flex', alignItems: 'center', gap: isMobile ? '0' : '5px',
                      padding: isMobile ? '8px' : '7px 12px', borderRadius: '8px',
                      border: '1px solid #e5e7eb', background: 'white',
                      color: '#374151', fontSize: '13px', fontWeight: '500',
                      cursor: isDeletingThis ? 'not-allowed' : 'pointer',
                      fontFamily: FONT, transition: 'all 0.15s',
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M4.8 2.3A.3.3 0 1 0 5 2H4a2 2 0 0 0-2 2v5a6 6 0 0 0 6 6 6 6 0 0 0 6-6V4a2 2 0 0 0-2-2h-1a.2.2 0 1 0 .3.3" />
                      <path d="M8 15v1a6 6 0 0 0 6 6v0a6 6 0 0 0 6-6v-4" />
                      <circle cx="20" cy="10" r="2" />
                    </svg>
                    {!isMobile && 'Share with Dr.'}
                  </button>
                )}

                {/* Edit button */}
                <button
                  className={`edit-btn${isEditOpen ? ' edit-btn-active' : ''}`}
                  onClick={() => isEditOpen ? setEditOpen(null) : openEdit(record)}
                  disabled={isDeletingThis}
                  title="Edit record details"
                  style={{
                    display: 'flex', alignItems: 'center', gap: isMobile ? '0' : '5px',
                    padding: isMobile ? '8px' : '7px 12px', borderRadius: '8px',
                    border: '1px solid #e5e7eb', background: 'white',
                    color: '#9ca3af', fontSize: '13px', fontWeight: '500',
                    cursor: isDeletingThis ? 'not-allowed' : 'pointer',
                    fontFamily: FONT, transition: 'all 0.15s',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  {!isMobile && 'Edit'}
                </button>

                {/* Download button */}
                <button
                  className="dl-btn"
                  onClick={() => handleDownload(record)}
                  disabled={!!dlState || isDeletingThis}
                  title="Download"
                  style={{
                    display: 'flex', alignItems: 'center', gap: isMobile ? '0' : '6px',
                    padding: isMobile ? '8px' : '7px 14px', borderRadius: '8px',
                    border: '1px solid #e5e7eb', background: 'white',
                    color: '#374151', fontSize: '13px', fontWeight: '500',
                    cursor: (dlState || isDeletingThis) ? 'not-allowed' : 'pointer',
                    fontFamily: FONT, transition: 'all 0.15s',
                    opacity: (dlState || isDeletingThis) ? 0.7 : 1,
                  }}
                >
                  {dlState ? (
                    <div style={{
                      width: '14px', height: '14px', borderRadius: '50%',
                      border: `2px solid ${T}`, borderTopColor: 'transparent',
                      animation: 'spin 0.7s linear infinite',
                    }} />
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                      </svg>
                      {!isMobile && (dlState === 'fetching' ? 'Fetching…' : dlState === 'decrypting' ? 'Decrypting…' : 'Download')}
                    </>
                  )}
                </button>

                {/* Delete button — two-click confirmation */}
                <button
                  className={isConfirming ? 'del-confirm' : 'del-btn'}
                  onClick={() => handleDeleteClick(record)}
                  disabled={isDeletingThis || !!dlState}
                  title={isConfirming ? 'Click again to confirm deletion' : 'Delete record'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px',
                    padding: '7px 12px', borderRadius: '8px',
                    border: `1px solid ${isConfirming ? '#fca5a5' : '#e5e7eb'}`,
                    background: isConfirming ? '#fee2e2' : 'white',
                    color: isConfirming ? '#dc2626' : '#9ca3af',
                    fontSize: '13px', fontWeight: isConfirming ? '600' : '400',
                    cursor: (isDeletingThis || dlState) ? 'not-allowed' : 'pointer',
                    fontFamily: FONT, transition: 'all 0.15s',
                    opacity: isDeletingThis ? 0.5 : 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {isDeletingThis ? (
                    <div style={{
                      width: '12px', height: '12px', borderRadius: '50%',
                      border: '2px solid #dc2626', borderTopColor: 'transparent',
                      animation: 'spin 0.7s linear infinite',
                    }} />
                  ) : isConfirming ? (
                    <>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Confirm?
                    </>
                  ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  )}
                </button>
              </div>
              </div>{/* end top row */}

              {/* Bottom row: encrypted badge + IPFS link */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                marginTop: '10px', paddingTop: '10px',
                borderTop: '1px solid #f3f4f6',
              }}>
                <span style={{
                  background: '#f0fdfb', color: T,
                  border: `1px solid ${T}30`,
                  padding: '2px 9px', borderRadius: '5px',
                  fontSize: '11px', fontWeight: '600',
                }}>
                  🔐 AES-256-GCM encrypted
                </span>
                <a
                  href={`https://ipfs.io/ipfs/${record.cid}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    fontSize: '11px', color: '#9ca3af', textDecoration: 'none',
                    transition: 'color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = T}
                  onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                  Verify on IPFS
                </a>
              </div>
            </div>

            {/* Share panel */}
            {isShareOpen && (
              <SharePanel
                record={record}
                userId={userId}
                onClose={() => setShareOpen(null)}
              />
            )}

            {/* Share with Provider panel */}
            {isProviderShareOpen && (
              <ShareWithProviderPanel
                record={record}
                userId={userId}
                userEmail={userEmail}
                wallets={wallets}
                onClose={() => setProviderShareOpen(null)}
              />
            )}

            {/* Edit panel */}
            {isEditOpen && (
              <div style={{
                background: '#fafafa', border: '1px solid #e5e7eb',
                borderRadius: '12px', padding: '20px 22px', marginTop: '4px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>Edit details</span>
                  </div>
                  <button onClick={() => setEditOpen(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '18px', lineHeight: 1, padding: '2px 4px' }}>×</button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Type */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '7px' }}>Record type</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {RECORD_TYPES.map(t => {
                        const active = (ef.recordType || record.metadata?.recordType) === t.value;
                        return (
                          <button
                            key={t.value}
                            onClick={() => setEditFields(prev => ({ ...prev, [record.id]: { ...prev[record.id], recordType: active ? null : t.value } }))}
                            disabled={isSaving}
                            style={{
                              padding: '5px 13px', borderRadius: '100px', border: `1px solid ${active ? t.color : '#e5e7eb'}`,
                              background: active ? t.bg : 'white', color: active ? t.color : '#6b7280',
                              fontSize: '12px', fontWeight: active ? '600' : '400', cursor: 'pointer',
                              fontFamily: FONT, transition: 'all 0.15s',
                            }}
                          >
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Name */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }}>Record name</label>
                    <input
                      type="text"
                      placeholder="e.g. Blood test, MRI scan"
                      value={ef.recordName || ''}
                      onChange={e => setEditFields(prev => ({ ...prev, [record.id]: { ...prev[record.id], recordName: e.target.value } }))}
                      disabled={isSaving}
                      style={{
                        width: '100%', padding: '9px 13px', border: '1px solid #e5e7eb', borderRadius: '8px',
                        fontSize: '14px', color: '#111827', background: 'white', fontFamily: FONT,
                        outline: 'none', boxSizing: 'border-box',
                      }}
                      onFocus={e => e.target.style.borderColor = T}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>

                  {/* Date */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }}>Date of record</label>
                    <input
                      type="date"
                      value={ef.recordDate || ''}
                      onChange={e => setEditFields(prev => ({ ...prev, [record.id]: { ...prev[record.id], recordDate: e.target.value } }))}
                      disabled={isSaving}
                      style={{
                        width: '100%', padding: '9px 13px', border: '1px solid #e5e7eb', borderRadius: '8px',
                        fontSize: '14px', color: '#111827', background: 'white', fontFamily: FONT,
                        outline: 'none', boxSizing: 'border-box',
                      }}
                      onFocus={e => e.target.style.borderColor = T}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>

                  {/* Notes */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '5px' }}>Notes</label>
                    <textarea
                      placeholder="e.g. Follow-up required, fasting required…"
                      value={ef.recordNotes || ''}
                      onChange={e => setEditFields(prev => ({ ...prev, [record.id]: { ...prev[record.id], recordNotes: e.target.value } }))}
                      disabled={isSaving}
                      rows={3}
                      style={{
                        width: '100%', padding: '9px 13px', border: '1px solid #e5e7eb', borderRadius: '8px',
                        fontSize: '14px', color: '#111827', background: 'white', fontFamily: FONT,
                        outline: 'none', boxSizing: 'border-box', resize: 'vertical', minHeight: '70px', lineHeight: '1.5',
                      }}
                      onFocus={e => e.target.style.borderColor = T}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                  <button
                    onClick={() => handleSaveEdit(record)}
                    disabled={isSaving}
                    style={{
                      flex: 1, padding: '10px 18px', background: isSaving ? '#e5e7eb' : T,
                      color: isSaving ? '#9ca3af' : 'white', border: 'none', borderRadius: '8px',
                      fontSize: '14px', fontWeight: '600', cursor: isSaving ? 'not-allowed' : 'pointer',
                      fontFamily: FONT, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                      transition: 'all 0.2s',
                    }}
                  >
                    {isSaving ? (
                      <><div style={{ width: '12px', height: '12px', borderRadius: '50%', border: '2px solid #9ca3af', borderTopColor: 'transparent', animation: 'spin 0.7s linear infinite' }} />Saving…</>
                    ) : 'Save changes'}
                  </button>
                  <button
                    onClick={() => setEditOpen(null)}
                    disabled={isSaving}
                    style={{
                      padding: '10px 16px', background: 'white', color: '#6b7280',
                      border: '1px solid #e5e7eb', borderRadius: '8px',
                      fontSize: '14px', fontWeight: '500', cursor: 'pointer', fontFamily: FONT,
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {err && (
              <div style={{
                padding: '10px 16px', background: '#fef2f2',
                border: '1px solid #fecaca', borderRadius: '8px',
                fontSize: '13px', color: '#dc2626',
              }}>
                {err}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Activity Tab ─────────────────────────────────────────────────────────────
const ACTION_META = {
  upload:        { label: 'Uploaded',      color: '#059669', bg: '#ecfdf5', icon: '↑' },
  download:      { label: 'Downloaded',    color: '#2563eb', bg: '#eff6ff', icon: '↓' },
  share_created: { label: 'Share created', color: T,         bg: '#f0fdfb', icon: '⤴' },
  share_viewed:  { label: 'Share viewed',  color: '#7c3aed', bg: '#f5f3ff', icon: '👁' },
  share_revoked: { label: 'Share revoked', color: '#dc2626', bg: '#fef2f2', icon: '✕' },
  delete:        { label: 'Deleted',       color: '#9ca3af', bg: '#f9fafb', icon: '🗑' },
};

function ActivityTab({ userId }) {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data } = await getAccessLog(userId);
      setLog(data || []);
      setLoading(false);
    })();
  }, [userId]);

  const formatTime = (ts) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', fontSize: '14px' }}>
      Loading activity…
    </div>
  );

  if (log.length === 0) return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{ fontSize: '32px', marginBottom: '12px' }}>📋</div>
      <div style={{ color: '#374151', fontWeight: '600', fontSize: '15px', marginBottom: '6px' }}>No activity yet</div>
      <div style={{ color: '#9ca3af', fontSize: '13px' }}>Uploads, downloads, and share events will appear here.</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {log.map(entry => {
        const meta = ACTION_META[entry.action] || { label: entry.action, color: '#6b7280', bg: '#f9fafb', icon: '•' };
        return (
          <div key={entry.id} style={{
            background: 'white', borderRadius: '12px',
            border: '1px solid #e5e7eb', padding: '14px 18px',
            display: 'flex', alignItems: 'center', gap: '14px',
          }}>
            {/* Icon */}
            <div style={{
              width: '36px', height: '36px', borderRadius: '9px', flexShrink: 0,
              background: meta.bg, border: `1px solid ${meta.color}30`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '15px', color: meta.color, fontWeight: '700',
            }}>
              {meta.icon}
            </div>

            {/* Details */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: '12px', fontWeight: '600', color: meta.color,
                  background: meta.bg, padding: '2px 8px', borderRadius: '100px',
                  border: `1px solid ${meta.color}30`,
                }}>
                  {meta.label}
                </span>
                {entry.file_name && (
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }}>
                    {entry.file_name}
                  </span>
                )}
              </div>
              {entry.share_token && (
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '3px', fontFamily: 'monospace' }}>
                  token: {entry.share_token.slice(0, 12)}…
                </div>
              )}
            </div>

            {/* Timestamp */}
            <div style={{ fontSize: '12px', color: '#9ca3af', flexShrink: 0, textAlign: 'right' }}>
              {formatTime(entry.created_at)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Requests Tab ────────────────────────────────────────────────────────────
function RequestsTab({ userEmail, userId }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});
  const [deleting, setDeleting] = useState({});

  const load = async () => {
    setLoading(true);
    // Fetch connections first
    const { data: connRows } = await supabase
      .from('connections')
      .select('*')
      .eq('patient_email', userEmail)
      .order('requested_at', { ascending: false });

    if (!connRows || connRows.length === 0) {
      setConnections([]);
      setLoading(false);
      return;
    }

    // Fetch provider details separately (no FK defined so PostgREST join won't work)
    const providerIds = [...new Set(connRows.map(c => c.provider_id))];
    const { data: provRows } = await supabase
      .from('providers')
      .select('user_id, first_name, last_name, credential, taxonomy, npi')
      .in('user_id', providerIds);

    const provMap = {};
    (provRows || []).forEach(p => { provMap[p.user_id] = p; });

    setConnections(connRows.map(c => ({ ...c, providers: provMap[c.provider_id] || {} })));
    setLoading(false);
  };

  useEffect(() => { load(); }, [userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const respond = async (id, status) => {
    setActing(a => ({ ...a, [id]: true }));
    await supabase
      .from('connections')
      .update({ status, patient_id: userId, responded_at: new Date().toISOString() })
      .eq('id', id);
    await load();
    setActing(a => ({ ...a, [id]: false }));
  };

  const formatDate = ts => new Date(ts).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af', fontSize: '14px' }}>
      Loading requests…
    </div>
  );

  if (connections.length === 0) return (
    <div style={{ textAlign: 'center', padding: '60px 0' }}>
      <div style={{ fontSize: '32px', marginBottom: '12px' }}>🩺</div>
      <div style={{ color: '#374151', fontWeight: '600', fontSize: '15px', marginBottom: '6px' }}>No connection requests</div>
      <div style={{ color: '#9ca3af', fontSize: '13px' }}>When a provider sends you a request it will appear here.</div>
    </div>
  );

  const pending = connections.filter(c => c.status === 'pending');
  const others = connections.filter(c => c.status !== 'pending');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {pending.length > 0 && (
        <div style={{ marginBottom: '4px' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', letterSpacing: '0.5px', marginBottom: '10px' }}>
            PENDING ({pending.length})
          </div>
          {pending.map(c => {
            const p = c.providers || {};
            const name = [p.first_name, p.last_name].filter(Boolean).join(' ');
            const isActing = acting[c.id];
            return (
              <div key={c.id} style={{
                background: 'white', borderRadius: '14px',
                border: `1px solid ${T}30`, padding: '20px 22px',
                display: 'flex', alignItems: 'center', gap: '16px',
                boxShadow: `0 2px 12px ${T}10`,
              }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0,
                  background: `${T}15`, border: `1px solid ${T}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '20px',
                }}>🩺</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '700', fontSize: '15px', color: '#111827' }}>
                    {name ? `${name}${p.credential ? `, ${p.credential}` : ''}` : 'Provider'}
                  </div>
                  {p.taxonomy && (
                    <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>{p.taxonomy}</div>
                  )}
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '3px' }}>
                    Requested {formatDate(c.requested_at)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                  <button
                    onClick={() => respond(c.id, 'declined')}
                    disabled={isActing}
                    style={{
                      padding: '8px 14px', borderRadius: '9px',
                      border: '1px solid #e5e7eb', background: 'white',
                      color: '#6b7280', fontSize: '13px', fontWeight: '600',
                      cursor: isActing ? 'not-allowed' : 'pointer', fontFamily: FONT,
                    }}
                  >
                    Decline
                  </button>
                  <button
                    onClick={() => respond(c.id, 'accepted')}
                    disabled={isActing}
                    style={{
                      padding: '8px 16px', borderRadius: '9px',
                      border: 'none', background: T,
                      color: 'white', fontSize: '13px', fontWeight: '600',
                      cursor: isActing ? 'not-allowed' : 'pointer', fontFamily: FONT,
                      boxShadow: `0 2px 8px ${T}40`,
                    }}
                  >
                    {isActing ? '…' : 'Accept'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {others.length > 0 && (
        <div>
          {pending.length > 0 && (
            <div style={{ fontSize: '12px', fontWeight: '700', color: '#6b7280', letterSpacing: '0.5px', marginBottom: '10px' }}>
              PREVIOUS
            </div>
          )}
          {others.map(c => {
            const p = c.providers || {};
            const name = [p.first_name, p.last_name].filter(Boolean).join(' ');
            const accepted = c.status === 'accepted';
            const isDeleting = deleting[c.id];
            return (
              <div key={c.id} style={{
                background: 'white', borderRadius: '12px',
                border: '1px solid #e5e7eb', padding: '16px 20px',
                display: 'flex', alignItems: 'center', gap: '14px',
                marginBottom: '8px',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>
                    {name ? `${name}${p.credential ? `, ${p.credential}` : ''}` : 'Provider'}
                  </div>
                  {p.taxonomy && (
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{p.taxonomy}</div>
                  )}
                </div>
                <span style={{
                  fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '100px',
                  background: accepted ? '#ecfdf5' : '#f9fafb',
                  color: accepted ? '#059669' : '#9ca3af',
                  border: `1px solid ${accepted ? '#6ee7b7' : '#e5e7eb'}`,
                }}>
                  {accepted ? 'Connected' : 'Declined'}
                </span>
                {!accepted && (
                  <button
                    disabled={isDeleting}
                    onClick={async () => {
                      setDeleting(d => ({ ...d, [c.id]: true }));
                      await supabase.from('connections').delete().eq('id', c.id);
                      await load();
                      setDeleting(d => ({ ...d, [c.id]: false }));
                    }}
                    title="Remove"
                    style={{
                      background: 'none', border: 'none', cursor: isDeleting ? 'not-allowed' : 'pointer',
                      color: '#9ca3af', fontSize: '18px', lineHeight: 1, padding: '2px 4px',
                      flexShrink: 0, opacity: isDeleting ? 0.4 : 1,
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Links Tab ────────────────────────────────────────────────────────────────
function LinksTab({ userId }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState({});
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await getAllShareLinksForUser(userId);
    setLinks(data || []);
    setLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (linkId) => {
    setRevoking(prev => ({ ...prev, [linkId]: true }));
    const link = links.find(l => l.id === linkId);
    await revokeShareLink(linkId);
    track('share_link_revoked', { source: 'links_tab' });
    logAccess({ action: 'share_revoked', userId, recordId: link?.record_id, fileName: link?.file_name, fileType: link?.file_type, shareToken: link?.token });
    await load();
    setRevoking(prev => ({ ...prev, [linkId]: false }));
  };

  const handleCopy = (link) => {
    const url = `${window.location.origin}/share/${link.token}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(link.id);
      setTimeout(() => setCopied(id => id === link.id ? null : id), 2000);
    });
  };

  const formatExpiry = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = d - now;
    const diffHours = diffMs / (1000 * 60 * 60);
    if (diffHours < 24) return `Expires in ${Math.round(diffHours)}h`;
    const diffDays = Math.round(diffHours / 24);
    if (diffDays === 1) return 'Expires tomorrow';
    return `Expires ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const formatLastViewed = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div style={{
        background: 'white', borderRadius: '16px', border: '1px solid #e5e7eb',
        padding: '60px 32px', textAlign: 'center',
      }}>
        <div style={{
          width: '20px', height: '20px', borderRadius: '50%',
          border: `2px solid ${T}`, borderTopColor: 'transparent',
          animation: 'spin 0.7s linear infinite', margin: '0 auto',
        }} />
      </div>
    );
  }

  if (links.length === 0) {
    return (
      <div style={{
        background: 'white', borderRadius: '16px', border: '1px solid #e5e7eb',
        padding: '72px 32px', textAlign: 'center',
      }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '16px',
          background: '#f0fdfb', border: `1px solid ${T}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
        </div>
        <h3 style={{ color: '#111827', fontSize: '17px', fontWeight: '600', margin: '0 0 8px 0' }}>
          No active share links
        </h3>
        <p style={{ color: '#9ca3af', fontSize: '14px', margin: 0, lineHeight: '1.6' }}>
          Share links you create from your records will appear here.<br />
          You can copy or revoke them any time.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
        <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
          {links.length} active {links.length === 1 ? 'link' : 'links'} · revoke any time to cut off access immediately
        </p>
        <button
          onClick={load}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#9ca3af', fontSize: '12px', fontFamily: FONT,
            display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 6px',
            borderRadius: '6px', transition: 'color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = T}
          onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
          Refresh
        </button>
      </div>

      {links.map(link => {
        const rt = getRecordType(link.record_type);
        const views = link.view_count || 0;
        const lastViewed = formatLastViewed(link.last_viewed_at);
        const isCopied = copied === link.id;
        const isRevoking = revoking[link.id];
        const expiryLabel = formatExpiry(link.expires_at);
        const isExpiringSoon = (new Date(link.expires_at) - new Date()) < 24 * 60 * 60 * 1000;

        return (
          <div key={link.id} style={{
            background: 'white', borderRadius: '12px',
            border: '1px solid #e5e7eb', padding: '16px 20px',
            transition: 'border-color 0.15s',
          }}>
            {/* Top row: file info + actions */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
              {/* Left: icon + name + type */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
                <div style={{
                  width: '38px', height: '38px', borderRadius: '9px', flexShrink: 0,
                  background: rt ? rt.bg : '#f0fdfb',
                  border: `1px solid ${rt ? rt.border : `${T}25`}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={rt ? rt.color : T} strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap', marginBottom: '3px' }}>
                    <span style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>
                      {link.file_name || 'Medical Record'}
                    </span>
                    {rt && (
                      <span style={{
                        fontSize: '11px', fontWeight: '600', padding: '1px 8px', borderRadius: '100px',
                        background: rt.bg, color: rt.color, border: `1px solid ${rt.border}`,
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}>
                        {rt.label}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: '11px', fontWeight: '500',
                      color: isExpiringSoon ? '#d97706' : '#9ca3af',
                    }}>
                      {isExpiringSoon && '⚠ '}{expiryLabel}
                    </span>
                    <span style={{ fontSize: '11px', color: '#e5e7eb' }}>·</span>
                    <span style={{ fontSize: '11px', color: views > 0 ? T : '#9ca3af', fontWeight: views > 0 ? '600' : '400' }}>
                      {views === 0 ? 'Not opened yet' : `Opened ${views} ${views === 1 ? 'time' : 'times'}`}
                    </span>
                    {lastViewed && (
                      <>
                        <span style={{ fontSize: '11px', color: '#e5e7eb' }}>·</span>
                        <span style={{ fontSize: '11px', color: '#9ca3af' }}>Last opened {lastViewed}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: buttons */}
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignItems: 'center' }}>
                <button
                  onClick={() => handleCopy(link)}
                  style={{
                    padding: '6px 12px', borderRadius: '7px',
                    border: `1px solid ${isCopied ? T : '#e5e7eb'}`,
                    background: isCopied ? `${T}12` : 'white',
                    color: isCopied ? T : '#374151',
                    fontSize: '12px', fontWeight: '600',
                    cursor: 'pointer', fontFamily: FONT, transition: 'all 0.2s',
                    display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                  }}
                >
                  {isCopied ? (
                    <>✓ Copied</>
                  ) : (
                    <>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                      </svg>
                      Copy link
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleRevoke(link.id)}
                  disabled={isRevoking}
                  style={{
                    padding: '6px 12px', borderRadius: '7px',
                    border: '1px solid #fca5a5',
                    background: isRevoking ? '#f9fafb' : '#fff5f5',
                    color: '#dc2626', fontSize: '12px', fontWeight: '600',
                    cursor: isRevoking ? 'not-allowed' : 'pointer',
                    fontFamily: FONT, transition: 'all 0.15s', whiteSpace: 'nowrap',
                  }}
                >
                  {isRevoking ? '…' : 'Revoke'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
function Dashboard({ userEmail, userId, onLogout }) {
  const { wallets } = useWallets();
  const { createWallet } = useCreateWallet();
  const migrationRan = useRef(false);
  const walletCreationAttempted = useRef(false);

  // If the user has no embedded wallet yet (e.g. it failed to create at login
  // because the domain wasn't authorized), trigger creation now.
  useEffect(() => {
    if (walletCreationAttempted.current) return;
    const hasEmbedded = wallets.some(w => w.walletClientType === 'privy');
    if (hasEmbedded) return;
    // Small delay to let Privy finish initializing before we try
    const t = setTimeout(async () => {
      walletCreationAttempted.current = true;
      try {
        await createWallet();
      } catch (e) {
        // Wallet may already exist — safe to ignore
      }
    }, 2000);
    return () => clearTimeout(t);
  }, [wallets, createWallet]);

  // ── ECDH key pair setup ───────────────────────────────────────────────────
  // Generate a P-256 ECDH key pair once on first login and store it in Supabase.
  // The public key is stored in plaintext so other users can encrypt for this user.
  // The private key is wrapped with this user's KEK before storage.
  const ecdhSetupRan = useRef(false);
  useEffect(() => {
    if (ecdhSetupRan.current || !isSupabaseConfigured || wallets.length === 0) return;
    const embeddedWallet = wallets.find(w => w.walletClientType === 'privy');
    if (!embeddedWallet) return;

    (async () => {
      try {
        // Check if this user already has an ECDH key pair stored
        const { data: userRow } = await supabase
          .from('users')
          .select('ecdh_public_key')
          .eq('user_id', userId)
          .single();

        if (userRow?.ecdh_public_key) {
          ecdhSetupRan.current = true;
          return; // Already set up
        }

        // Generate a new P-256 ECDH key pair
        const kek = await getOrDeriveKEK(embeddedWallet, userId);
        const { publicKeyB64, privateKey } = await generateECDHKeyPair();
        const wrappedPrivateKeyB64 = await wrapECDHPrivateKey(privateKey, kek);

        // Store in the users table
        await supabase
          .from('users')
          .upsert({
            user_id: userId,
            ecdh_public_key: publicKeyB64,
            ecdh_private_key_wrapped: wrappedPrivateKeyB64,
          }, { onConflict: 'user_id' });

        ecdhSetupRan.current = true;
      } catch (e) {
        // Non-fatal — will retry on next login
      }
    })();
  }, [wallets, userId]);

  const [activeTab, setActiveTab] = useState('upload');
  const [records, setRecords] = useState([]);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);

  // Keep pending connection request badge up to date
  useEffect(() => {
    if (!userEmail || !isSupabaseConfigured) return;
    supabase
      .from('connections')
      .select('id', { count: 'exact', head: true })
      .eq('patient_email', userEmail)
      .eq('status', 'pending')
      .then(({ count }) => setPendingRequestCount(count || 0));
  }, [userEmail]);

  // Namespace by user ID for localStorage fallback
  const storageKey = `vewr_records_${userId}`;

  useEffect(() => {
    track('portal_viewed', { user_id: userId });
  }, [userId]);

  // Register / update the user row so the owner can see who has signed up
  useEffect(() => {
    if (!isSupabaseConfigured || !userId || !userEmail) return;
    supabase
      .from('users')
      .upsert(
        { user_id: userId, email: userEmail, last_seen_at: new Date().toISOString() },
        { onConflict: 'user_id' }
      )
      .then(() => {}); // fire and forget
  }, [userId, userEmail]);

  useEffect(() => {
    const loadRecords = async () => {
      if (isSupabaseConfigured) {
        // Fetch from Supabase — works across all devices
        const { data, error } = await supabase
          .from('records')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });
        if (!error && data) {
          setRecords(data.map(row => ({
            id: row.id,
            cid: row.cid,
            metadata: row.metadata,
            uploadedAt: row.created_at,
          })));
        }
      } else {
        // Fallback: browser localStorage (device-only)
        try {
          const stored = localStorage.getItem(storageKey);
          if (stored) setRecords(JSON.parse(stored));
        } catch (e) {}
      }
    };
    loadRecords();
  }, [userId, storageKey]);

  // ── Background migration: upgrade v1 records to KEK-wrapped keys ──────────
  useEffect(() => {
    if (migrationRan.current || records.length === 0 || wallets.length === 0) return;

    const v1Records = records.filter(r =>
      (!r.metadata?.keyVersion || r.metadata.keyVersion < 2) && r.metadata?.encryptedKey
    );
    if (v1Records.length === 0) return;

    migrationRan.current = true;

    (async () => {
      try {
        const embeddedWallet = wallets.find(w => w.walletClientType === 'privy');
        if (!embeddedWallet) return;

        const kek = await getOrDeriveKEK(embeddedWallet, userId);

        for (const record of v1Records) {
          try {
            const fileKey = await importPlainKey(record.metadata.encryptedKey);
            const wrappedKey = await wrapFileKey(fileKey, kek);
            const updatedMeta = { ...record.metadata, encryptedKey: wrappedKey, keyVersion: 2 };
            if (isSupabaseConfigured) {
              await supabase.from('records').update({ metadata: updatedMeta }).eq('id', record.id);
            }
            setRecords(prev => prev.map(r =>
              r.id === record.id ? { ...r, metadata: updatedMeta } : r
            ));
          } catch (e) {
            // Individual record migration failure — skip and continue
          }
        }
      } catch (e) {
        migrationRan.current = false;
      }
    })();
  }, [records, wallets, userId]);

  const handleDelete = async (record) => {
    const { id, cid } = record;

    // 1. Unpin from Pinata (best-effort — don't block on failure)
    const pinataJwt = process.env.REACT_APP_PINATA_JWT;
    if (pinataJwt && cid) {
      try {
        await fetch(`https://api.pinata.cloud/pinning/unpin/${cid}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${pinataJwt}` },
        });
      } catch (e) {
        // Non-fatal — file may already be unpinned or network issue
      }
    }

    // 2. Delete from Supabase or localStorage
    if (isSupabaseConfigured) {
      await supabase.from('records').delete().eq('id', id);
    } else {
      const updated = records.filter(r => r.id !== id);
      try { localStorage.setItem(storageKey, JSON.stringify(updated)); } catch (e) {}
    }

    // 3. Remove from UI
    track('record_deleted');
    logAccess({ action: 'delete', userId, recordId: id, fileName: record.metadata?.originalFileName, fileType: record.metadata?.originalFileType });
    setRecords(prev => prev.filter(r => r.id !== id));
  };

  const handleUploadSuccess = async (record) => {
    if (isSupabaseConfigured) {
      // Save to Supabase
      const { data, error } = await supabase
        .from('records')
        .insert({
          user_id: userId,
          cid: record.cid,
          metadata: record.metadata,
        })
        .select()
        .single();
      if (!error && data) {
        setRecords(prev => [{
          id: data.id,
          cid: data.cid,
          metadata: data.metadata,
          uploadedAt: data.created_at,
        }, ...prev]);
      }
    } else {
      // Fallback: localStorage
      const updated = [record, ...records];
      setRecords(updated);
      try {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      } catch (e) {}
    }
    track('record_uploaded', { file_type: record.metadata?.originalFileType });
    logAccess({ action: 'upload', userId, recordId: record.id, fileName: record.metadata?.originalFileName, fileType: record.metadata?.originalFileType });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7f9', fontFamily: FONT }}>
      {/* Top Nav */}
      <nav style={{
        background: '#0d1117', padding: '0 32px',
        height: '60px', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '32px', height: '32px', borderRadius: '8px',
            background: T, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ShieldIcon size={16} />
          </div>
          <span style={{ color: 'white', fontWeight: '700', fontSize: '16px', letterSpacing: '-0.3px' }}>
            Vewr Health
          </span>
          <span style={{
            fontSize: '11px', color: T, background: `${T}18`,
            border: `1px solid ${T}40`, padding: '2px 8px',
            borderRadius: '100px', marginLeft: '4px', fontWeight: '600',
          }}>
            Patient Portal
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {userEmail && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%',
                background: `${T}30`, border: `1px solid ${T}60`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: '700', color: T,
              }}>
                {userEmail[0]?.toUpperCase()}
              </div>
              <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px' }}>
                {userEmail}
              </span>
            </div>
          )}
          <button
            onClick={onLogout}
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.5)',
              padding: '6px 14px', borderRadius: '8px',
              fontSize: '13px', cursor: 'pointer', fontFamily: FONT,
            }}
          >
            Sign out
          </button>
        </div>
      </nav>

      {/* Main */}
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '48px 32px' }}>
        {/* Page header */}
        <div style={{ marginBottom: '36px' }}>
          <h1 style={{
            fontSize: '28px', fontWeight: '700', color: '#111827',
            margin: '0 0 8px 0', letterSpacing: '-0.5px',
          }}>
            Your Health Vault
          </h1>
          <p style={{ color: '#6b7280', fontSize: '15px', margin: 0 }}>
            Files are encrypted on your device before upload. Only you can access them.
          </p>
        </div>

        {/* Stats strip */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '14px', marginBottom: '36px',
        }}>
          {[
            {
              label: 'Records stored', value: records.length.toString(),
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              ),
            },
            {
              label: 'Encryption', value: 'AES-256-GCM',
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              ),
            },
            {
              label: 'Stored on', value: 'IPFS',
              icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T} strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              ),
            },
          ].map(stat => (
            <div key={stat.label} style={{
              background: 'white', borderRadius: '12px',
              padding: '20px 22px', border: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', gap: '14px',
            }}>
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                background: '#f0fdfb', border: `1px solid ${T}25`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {stat.icon}
              </div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#111827', letterSpacing: '-0.3px' }}>
                  {stat.value}
                </div>
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                  {stat.label}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tab switcher */}
        <div style={{
          display: 'flex', gap: '4px', marginBottom: '20px',
          background: '#e5e7eb', borderRadius: '10px',
          padding: '4px', width: 'fit-content',
        }}>
          {[
            { id: 'upload', label: 'Upload New' },
            { id: 'records', label: `My Records (${records.length})` },
            { id: 'links', label: 'Share Links' },
            { id: 'activity', label: 'Activity' },
            { id: 'requests', label: 'Requests', badge: pendingRequestCount },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '8px 20px', borderRadius: '7px', border: 'none',
                cursor: 'pointer', fontSize: '14px', fontWeight: '500', fontFamily: FONT,
                background: activeTab === tab.id ? 'white' : 'transparent',
                color: activeTab === tab.id ? '#111827' : '#6b7280',
                boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.15s',
                display: 'flex', alignItems: 'center', gap: '6px', position: 'relative',
              }}
            >
              {tab.label}
              {tab.badge > 0 && (
                <span style={{
                  background: T, color: 'white', fontSize: '11px', fontWeight: '700',
                  borderRadius: '100px', padding: '1px 6px', lineHeight: '1.4',
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'upload' && <UploadRecord onUploadSuccess={handleUploadSuccess} />}
        {activeTab === 'records' && (
          <RecordsList
            records={records}
            onDelete={handleDelete}
            onUpdate={(id, updatedMetadata) => setRecords(prev => prev.map(r => r.id === id ? { ...r, metadata: updatedMetadata } : r))}
            userId={userId}
            userEmail={userEmail}
          />
        )}
        {activeTab === 'links' && <LinksTab userId={userId} />}
        {activeTab === 'activity' && <ActivityTab userId={userId} />}
        {activeTab === 'requests' && <RequestsTab userEmail={userEmail} userId={userId} />}
      </div>
    </div>
  );
}

// ─── Privy-gated portal (hook must be called unconditionally inside here) ─────
function PrivyGatedPortal() {
  const { ready, authenticated, user, login, logout } = usePrivy();

  if (!ready) {
    return (
      <div style={{
        minHeight: '100vh', background: '#0d1117',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: FONT,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: 'rgba(255,255,255,0.4)' }}>
          <div style={{
            width: '20px', height: '20px', borderRadius: '50%',
            border: `2px solid ${T}`, borderTopColor: 'transparent',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          Loading…
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <LoginScreen onLogin={() => { track('login_started'); login(); }} />;
  }

  const userEmail =
    user?.email?.address ||
    user?.linkedAccounts?.find(a => a.type === 'email')?.address ||
    '';

  return <Dashboard userEmail={userEmail} userId={user?.id || 'anonymous'} onLogout={() => { track('logout'); logout(); }} />;
}

// ─── Main export ──────────────────────────────────────────────────────────────
function PatientHome() {
  if (!PRIVY_CONFIGURED) {
    return <SetupNotice />;
  }
  return <PortalGate><PrivyGatedPortal /></PortalGate>;
}

export default PatientHome;
